import { z } from "zod";

const guidSchema = z.string();
const nullableTextSchema = z.string().nullable().optional();
const nullableDateSchema = z.string().nullable().optional();

export const rcaRecordSchema = z.object({
  id: guidSchema,
  complaintId: guidSchema,
  complaintCode: z.string().optional(),
  complaintTitle: z.string().optional(),
  rcaCode: z.string().min(1),
  rcaMethodId: guidSchema,
  rcaMethodCode: z.string().min(1),
  rcaMethodName: z.string().min(1),
  rootCauseCategoryId: guidSchema.optional(),
  rootCauseCategoryCode: z.string().optional(),
  rootCauseCategoryName: z.string().optional(),
  problemStatement: z.string().min(1),
  problemDescription: nullableTextSchema,
  investigationSummary: nullableTextSchema,
  rootCause: nullableTextSchema,
  rootCauseVerified: z.boolean().optional(),
  confidenceScore: z.number().optional(),
  severity: z.string().optional(),
  priorityLevel: z.string().optional(),
  businessImpact: nullableTextSchema,
  customerImpact: nullableTextSchema,
  safetyImpact: nullableTextSchema,
  ownerUserId: guidSchema,
  ownerName: z.string().min(1),
  teamLeadUserId: guidSchema.optional(),
  teamLeadName: z.string().optional(),
  targetDate: z.string(),
  daysRemaining: z.number().optional(),
  isOverdue: z.boolean().optional(),
  slaStatusText: z.string().optional(),
  slaDaysText: z.string().optional(),
  statusId: guidSchema,
  statusCode: z.string().min(1),
  statusName: z.string().min(1),
  tags: z.array(z.string()).optional(),
  evidenceCount: z.number().optional(),
  actionCount: z.number().optional(),
  teamSize: z.number().optional(),
  completedAt: nullableDateSchema,
  createdAt: z.string(),
  updatedAt: nullableDateSchema,
  rowVersion: nullableTextSchema,
  allowedActions: z.array(z.string()).optional(),
}).passthrough();

export type RcaRecordDto = z.infer<typeof rcaRecordSchema>;
export type RcaDto = RcaRecordDto;
export type RcaMutationResponse = RcaRecordDto;

export type RcaMethodCode = 
  | "FIVE_WHY"
  | "FISHBONE"
  | "FTA"
  | "PARETO"
  | "8D"
  | "PROCESS_MAP"
  | "IS_IS_NOT"
  | "BRAINSTORMING"
  | "FMEA_REF";

export type RcaCategory6M = 
  | "MAN"
  | "MACHINE"
  | "METHOD"
  | "MATERIAL"
  | "MEASUREMENT"
  | "ENVIRONMENT";

/* ============================================================
 * 1. Five Why Analysis Types
 * ============================================================ */
export type FiveWhyStep = {
  id: string;
  whyNumber: number;
  question: string;
  answer: string;
  category: RcaCategory6M;
  evidenceDescription?: string | undefined;
  evidenceIds?: string[] | undefined;
  isRootCause: boolean;
  rootCauseJustification?: string | undefined;
  confidenceLevel: number; // 0-100
  alternativeAnswers?: string[] | undefined;
  notes?: string | undefined;
};

export type FiveWhySession = {
  id: string;
  rcaRecordId: string;
  problemStatement: string;
  initialSymptom?: string | undefined;
  steps: FiveWhyStep[];
  rootCauseIdentified: boolean;
  rootCauseWhyNumber?: number | undefined;
  rootCauseDescription?: string | undefined;
  verificationQuestion?: string | undefined;
  verificationAnswer?: string | undefined;
  confidenceScore: number;
  status: "IN_PROGRESS" | "COMPLETED" | "VERIFIED";
};

/* ============================================================
 * 2. Fishbone (Ishikawa) Analysis Types
 * ============================================================ */
export type FishboneCause = {
  id: string;
  categoryId: string;
  parentCauseId?: string | undefined;
  causeDescription: string;
  level: number;
  isRootCause: boolean;
  rootCauseJustification?: string | undefined;
  probabilityScore?: number | undefined;
  impactScore?: number | undefined;
  votesCount?: number | undefined;
  evidenceIds?: string[] | undefined;
  verificationStatus?: "NOT_VERIFIED" | "VERIFIED" | "REJECTED" | undefined;
  assignedTo?: string | undefined;
  notes?: string | undefined;
};

export type FishboneCategory = {
  id: string;
  code: string;
  name: string;
  category6M: RcaCategory6M;
  causes: FishboneCause[];
};

export type FishboneDiagram = {
  id: string;
  rcaRecordId: string;
  diagramTitle: string;
  problemStatement: string;
  framework: "6M" | "4P" | "CUSTOM";
  categories: FishboneCategory[];
  totalCausesCount: number;
  rootCausesCount: number;
  votingEnabled: boolean;
  status: "IN_PROGRESS" | "VOTING" | "COMPLETED";
};

/* ============================================================
 * 3. Fault Tree Analysis (FTA) Types
 * ============================================================ */
export type FtaNodeType = "TOP_EVENT" | "AND_GATE" | "OR_GATE" | "BASIC_EVENT" | "INTERMEDIATE_EVENT";

export type FtaNode = {
  id: string;
  parentId?: string | undefined;
  type: FtaNodeType;
  label: string;
  description?: string | undefined;
  probability?: number | undefined;
  probabilitySource?: string | undefined;
  isRootContributor?: boolean | undefined;
  evidenceIds?: string[] | undefined;
  children?: FtaNode[] | undefined;
};

export type FtaCutSet = {
  id: string;
  cutSetNumber: number;
  order: number;
  eventLabels: string[];
  calculatedProbability: number;
  criticalityRank: number;
};

export type FtaAnalysis = {
  id: string;
  rcaRecordId: string;
  topEvent: string;
  analysisType: "QUALITATIVE" | "QUANTITATIVE" | "HYBRID";
  rootNode: FtaNode;
  calculatedTopProbability?: number | undefined;
  cutSets: FtaCutSet[];
  expertReviewer?: string | undefined;
  expertReviewed: boolean;
  status: "IN_PROGRESS" | "REVIEWED" | "COMPLETED";
};

/* ============================================================
 * 4. Pareto Analysis Types
 * ============================================================ */
export type ParetoDataPoint = {
  id: string;
  defectType: string;
  count: number;
  percentage: number;
  cumulativePercentage: number;
  isVitalFew: boolean;
  costImpact?: number | undefined;
  chainedMethod?: RcaMethodCode | undefined;
};

export type ParetoAnalysis = {
  id: string;
  rcaRecordId: string;
  title: string;
  measurementType: "FREQUENCY" | "COST" | "TIME";
  thresholdPercentage: number; // default 80
  dataPoints: ParetoDataPoint[];
  totalCount: number;
  vitalFewSummary: string;
  status: "IN_PROGRESS" | "COMPLETED";
};

/* ============================================================
 * 5. 8D Problem Solving Types
 * ============================================================ */
export type EightDDisciplineCode = "D0" | "D1" | "D2" | "D3" | "D4" | "D5" | "D6" | "D7" | "D8";

export type EightDDiscipline = {
  code: EightDDisciplineCode;
  name: string;
  title: string;
  status: "NOT_STARTED" | "IN_PROGRESS" | "COMPLETED" | "VERIFIED" | "SKIPPED";
  summary: string;
  ownerName?: string | undefined;
  targetDate?: string | undefined;
  completionDate?: string | undefined;
  evidenceIds?: string[] | undefined;
  data: Record<string, unknown>;
};

export type EightDReport = {
  id: string;
  rcaRecordId: string;
  reportNumber: string;
  customerName?: string | undefined;
  customerReference?: string | undefined;
  partNumber?: string | undefined;
  partName?: string | undefined;
  lotNumber?: string | undefined;
  teamLeader: string;
  deadlineDate: string;
  disciplines: EightDDiscipline[];
  customerApproved: boolean;
  status: "IN_PROGRESS" | "CUSTOMER_REVIEW" | "CLOSED";
};

/* ============================================================
 * 6. Process Mapping Types
 * ============================================================ */
export type ProcessStepType = "PROCESS" | "DECISION" | "INSPECTION" | "REWORK" | "TRANSPORT" | "STORAGE" | "DELAY";

export type ProcessStep = {
  id: string;
  stepNumber: number;
  label: string;
  type: ProcessStepType;
  responsibleRole: string;
  cycleTimeMinutes?: number | undefined;
  valueAdded: "VA" | "NVA" | "NNVA";
  isFailurePoint: boolean;
  failureDescription?: string | undefined;
  nextStepIds: string[];
  evidenceIds?: string[] | undefined;
};

export type ProcessMap = {
  id: string;
  rcaRecordId: string;
  processName: string;
  scope: string;
  steps: ProcessStep[];
  failurePointsCount: number;
  status: "IN_PROGRESS" | "COMPLETED";
};

/* ============================================================
 * 7. Is / Is Not Analysis Types
 * ============================================================ */
export type IsIsNotDimension = {
  dimension: "WHAT" | "WHERE" | "WHEN" | "EXTENT" | "WHO" | "HOW";
  isFact: string;
  isNotFact: string;
  distinction: string;
  changeObserved?: string | undefined;
  evidenceText?: string | undefined;
};

export type IsIsNotHypothesis = {
  id: string;
  hypothesis: string;
  consistentWithWhat: boolean;
  consistentWithWhere: boolean;
  consistentWithWhen: boolean;
  consistentWithExtent: boolean;
  isSurviving: boolean;
  testNotes?: string | undefined;
};

export type IsIsNotAnalysis = {
  id: string;
  rcaRecordId: string;
  problemStatement: string;
  dimensions: IsIsNotDimension[];
  hypotheses: IsIsNotHypothesis[];
  survivingHypothesis?: string | undefined;
  status: "IN_PROGRESS" | "COMPLETED";
};

/* ============================================================
 * 8. Brainstorming Types
 * ============================================================ */
export type BrainstormIdea = {
  id: string;
  text: string;
  authorName: string;
  color: "yellow" | "blue" | "green" | "pink" | "orange" | "purple";
  clusterId?: string | undefined;
  clusterName?: string | undefined;
  votesCount: number;
  isShortlisted: boolean;
  convertedToCause?: boolean | undefined;
};

export type BrainstormSession = {
  id: string;
  rcaRecordId: string;
  objective: string;
  facilitator: string;
  method: "FREE_ASSOCIATION" | "ROUND_ROBIN" | "BRAINWRITING" | "REVERSE";
  ideas: BrainstormIdea[];
  totalIdeasCount: number;
  shortlistedCount: number;
  votingActive: boolean;
  status: "IDEATION" | "CLUSTERING" | "VOTING" | "SHORTLISTED" | "COMPLETED";
};

/* ============================================================
 * 9. FMEA Reference Types
 * ============================================================ */
export type FmeaReference = {
  id: string;
  rcaRecordId: string;
  fmeaType: "PFMEA" | "DFMEA" | "SYSTEM_FMEA";
  documentNumber: string;
  revision: string;
  failureMode: string;
  currentSeverity: number;
  currentOccurrence: number;
  currentDetection: number;
  actionPriority: "HIGH" | "MEDIUM" | "LOW";
  updateRequired: boolean;
  updateReason?: string | undefined;
  assignedChangeOwner?: string | undefined;
  status: "LINKED" | "REVIEW_PENDING" | "UPDATED";
};

/* ============================================================
 * 10. Evidence Center Types
 * ============================================================ */
export type RcaEvidenceItem = {
  id: string;
  fileName: string;
  fileType: "PDF" | "PNG" | "JPG" | "JPEG" | "XLSX" | "DOCX" | "CSV" | "MP4";
  fileSizeText: string;
  sizeBytes: number;
  scanStatus: "Clean" | "Scanning" | "Quarantined";
  linkedTo: string;
  uploadedBy: string;
  uploadedDate: string;
  sha256Hash: string;
  mimeType: string;
  isDeleted?: boolean | undefined;
};

/* ============================================================
 * 11. Actions / CAPA Handoff Types
 * ============================================================ */
export type RcaActionItem = {
  id: string;
  actionCode: string;
  description: string;
  type: "Corrective" | "Preventive" | "Containment";
  ownerName: string;
  targetDate: string;
  status: "Open" | "In Progress" | "Completed" | "Overdue";
  effectiveness: "Verified" | "Pending" | "Ineffective" | "—";
};

export type RcaCapaHandoff = {
  capaRequired: boolean;
  capaReference?: string | undefined;
  handedOverOn?: string | undefined;
  handoffBy?: string | undefined;
  notes: string;
  status: "NOT_HANDED_OVER" | "HANDED_OVER" | "IN_CAPA";
};

/* ============================================================
 * 12. Team & Collaboration Types
 * ============================================================ */
export type RcaTeamMember = {
  id: string;
  name: string;
  role: "RCA Owner" | "Team Lead" | "Member" | "Reviewer" | "Contributor";
  department: string;
  responsibilities: string;
  access: "Full Access" | "Edit" | "View";
  status: "Active" | "Inactive";
  avatarInitials: string;
};

export type RcaTeamNote = {
  id: string;
  authorName: string;
  timestamp: string;
  message: string;
  category?: "DISCUSSION" | "TASK" | "MENTION" | undefined;
};

/* ============================================================
 * 13. Review & Approval Types
 * ============================================================ */
export type RcaReviewChecklistItem = {
  id: string;
  label: string;
  status: "Yes" | "No" | "Pending" | "Overdue";
};

export type RcaReviewData = {
  reviewedBy: string;
  reviewLevel: string;
  reviewType: string;
  reviewDate: string;
  checklist: RcaReviewChecklistItem[];
  comments: string;
  decision?: "APPROVE" | "REJECT" | "REWORK" | undefined;
  conditions?: string | undefined;
};

/* ============================================================
 * 14. Audit History Types
 * ============================================================ */
export type RcaAuditEvent = {
  id: string;
  dateTime: string;
  action: string;
  performedBy: string;
  details: string;
  category: "ALL" | "STATUS" | "SUBMISSIONS";
};

/* ============================================================
 * Request / Mutation Types
 * ============================================================ */
export type RcaCreateRequest = Readonly<{
  complaintId: string;
  rcaMethodId: string;
  rootCauseCategoryId?: string | undefined;
  problemStatement: string;
  problemDescription?: string | undefined;
  severity?: string | undefined;
  priorityLevel?: string | undefined;
  businessImpact?: string | undefined;
  customerImpact?: string | undefined;
  safetyImpact?: string | undefined;
  investigationSummary?: string | undefined;
  rootCause?: string | undefined;
  ownerUserId: string;
  teamLeadUserId?: string | undefined;
  targetDate: string;
  statusId?: string | undefined;
  tags?: string[] | undefined;
}>;

export type RcaUpdateRequest = Readonly<{
  problemStatement?: string | undefined;
  problemDescription?: string | undefined;
  investigationSummary?: string | undefined;
  rootCause?: string | undefined;
  rootCauseVerified?: boolean | undefined;
  confidenceScore?: number | undefined;
  rcaMethodId?: string | undefined;
  rootCauseCategoryId?: string | undefined;
  ownerUserId?: string | undefined;
  targetDate?: string | undefined;
  rowVersion?: string | undefined;
  tags?: string[] | undefined;
}>;

export type RcaActionRequest = Readonly<{
  remarks?: string | undefined;
  reason?: string | undefined;
  investigationSummary?: string | undefined;
  rootCause?: string | undefined;
  completedStatusId?: string | undefined;
  rowVersion?: string | undefined;
}>;

export function parseRcaRecord(input: unknown): RcaRecordDto {
  return rcaRecordSchema.parse(input);
}

export function parseRcaRecordList(input: unknown): readonly RcaRecordDto[] {
  return z.array(rcaRecordSchema).parse(input);
}
